import {
    createFeatureReducer,
    entityListReducer,
    FeatureActionReducer,
    PayloadAction
} from '../../../store/store.utils';
import {
    ApiError,
    ApiResponse
} from '../../../../../common/models/api-response.interface';
import {
    AdvancedFilterField,
    BasicFilters,
    Event,
    EventAction,
    EventListResponse,
    EventSearchRequestPayload,
    BulkEditModel,
    GridPagination
} from '../../../../../common/models/event.interface';
import { Pid } from '../../../../../common/models/pid.interface';
import { difference, keyBy, cloneDeep } from 'lodash-es';
import { EventState, initialEventState } from '../event.state';
import { TextSearchSelection, AdvancedFilters } from 'quotient-ng-core';

function setEventListLoading(
    uiState: EventState['ui'],
    flag: boolean
): EventState['ui'] {
    return {
        ...uiState,
        eventGrid: {
            ...uiState.eventGrid,
            loading: flag
        }
    };
}

export abstract class EventActionReducer extends FeatureActionReducer<EventState> {
    protected constructor(name: string) {
        super('Event', name);
    }
}

export class EventListSearchChangeRequest
    extends EventActionReducer
    implements PayloadAction<EventSearchRequestPayload> {
    constructor(public readonly payload: EventSearchRequestPayload) {
        super('Event Search Change List Request');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: setEventListLoading(state.ui, true)
        };
    }
}

export class EventListPageChangeRequest
    extends EventActionReducer
    implements PayloadAction<EventSearchRequestPayload> {
    constructor(public readonly payload: EventSearchRequestPayload) {
        super('Event Pagination Change List Request');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: setEventListLoading(state.ui, true)
        };
    }
}

export class GetEventListForPaginationChangeSuccess
    extends EventActionReducer
    implements PayloadAction<EventListResponse> {
    constructor(public readonly payload: EventListResponse) {
        super('Event list Fetch Successful on Pagination Change');
    }

    reduce(state: EventState): EventState {
        return {
            data: {
                ...state.data,
                events: entityListReducer(
                    state.data.events,
                    this.payload.events,
                    'id'
                )
            },
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: false,
                    total: this.payload.total
                },
                eventDetail: {
                    ...state.ui.eventDetail,
                    uiMap: {}
                }
            }
        };
    }
}

export class GetPartialEventListRequest
    extends EventActionReducer
    implements PayloadAction<number[]> {
    constructor(public readonly payload: number[]) {
        super('Partial List Request');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: setEventListLoading(state.ui, true)
        };
    }
}

export class GetEventListRequestSuccess
    extends EventActionReducer
    implements PayloadAction<EventListResponse> {
    constructor(public readonly payload: EventListResponse) {
        super('Search List Success');
    }

    reduce(state: EventState): EventState {
        return {
            data: {
                ...state.data,
                events: entityListReducer(
                    state.data.events,
                    this.payload.events,
                    'id'
                )
            },
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: false,
                    pagination: {
                        ...state.ui.eventGrid.pagination,
                        skip: 0
                    },
                    total: this.payload.total
                },
                eventDetail: {
                    ...state.ui.eventDetail,
                    uiMap: {}
                }
            }
        };
    }
}

export class GetPartialEventListRequestSuccess
    extends EventActionReducer
    implements PayloadAction<Event[]> {
    constructor(public readonly payload: Event[]) {
        super('Partial List Success');
    }

    reduce(state: EventState): EventState {
        return {
            data: {
                ...state.data,
                events: entityListReducer(
                    state.data.events,
                    this.payload,
                    'id',
                    false
                )
            },
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: false
                }
            }
        };
    }
}

export class EventListSortRequest
    extends EventActionReducer
    implements PayloadAction<EventSearchRequestPayload> {
    constructor(public readonly payload: EventSearchRequestPayload) {
        super('Event Sort Change List Request');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: true
                }
            }
        };
    }
}

export class GetEventListRequestFailure
    extends EventActionReducer
    implements PayloadAction<ApiError> {
    constructor(public readonly payload: ApiError) {
        super('Event List Failure');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: false
                }
            }
        };
    }
}

export interface EventActionPayload {
    eventIDs: number[];
    action: EventAction;
    editedInfo?: BulkEditModel;
}

export class EventActionRequest
    extends EventActionReducer
    implements PayloadAction<EventActionPayload> {
    constructor(public readonly payload: EventActionPayload) {
        super('Event Action Request');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: true
                }
            }
        };
    }
}

export class EventActionSuccess extends EventActionReducer {
    constructor() {
        super('Event Action Success');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: false
                }
            }
        };
    }
}

export class EventActionRequestFailure
    extends EventActionReducer
    implements PayloadAction<ApiError> {
    constructor(public readonly payload: ApiError) {
        super('Event Action Request Failure');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    loading: false
                }
            }
        };
    }
}

export class EventDownloadByIdListRequest
    extends EventActionReducer
    implements PayloadAction<number[]> {
    constructor(public readonly payload: number[]) {
        super('Event Download By Event ID List Action Request');
    }

    reduce(state: EventState): EventState {
        return state;
    }
}

export class EventDownloadBySearchCriteriaRequest
    extends EventActionReducer
    implements PayloadAction<EventSearchRequestPayload> {
    constructor(public readonly payload: EventSearchRequestPayload) {
        super('Download Event List By Search Criteria Request');
    }

    reduce(state: EventState): EventState {
        return state;
    }
}

export class UPCDiffDownloadRequest
    extends EventActionReducer
    implements PayloadAction<any> {
    constructor(public readonly payload: any) {
        super('UPC DIff Download Action Request');
    }

    reduce(state: EventState): EventState {
        return state;
    }
}

export class UpdateEventUIFromLocalStorage
    extends EventActionReducer
    implements PayloadAction<EventState['ui']> {
    constructor(public readonly payload: EventState['ui']) {
        super('Load From Local Storage');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: this.payload
        };
    }
}

export class UpdateEventUIPagination
    extends EventActionReducer
    implements PayloadAction<any> {
    constructor(public readonly payload: GridPagination) {
        super('Set Pagination');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    pagination: { ...this.payload }
                }
            }
        };
    }
}

export class ClearAllSearchFilters extends EventActionReducer {
    constructor() {
        super('Set Event Advanced Filters');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventSearchCriteria: {
                    ...state.ui.eventSearchCriteria,
                    keyword: {
                        ...state.ui.eventSearchCriteria.keyword,
                        searchString: ''
                    },
                    basicFilters: {
                        ...state.ui.eventSearchCriteria.basicFilters,
                        groupList: state.ui.eventSearchCriteria.basicFilters.groupList.map(
                            group => {
                                return { groupKey: group.groupKey, items: [] };
                            }
                        ),
                        checkedCount: 0
                    },
                    advancedFilters: {
                        ...state.ui.eventSearchCriteria.advancedFilters,
                        filters: [],
                        filtersActive: 0
                    }
                }
            }
        };
    }
}

export class UpdateEventAdvancedFilters
    extends EventActionReducer
    implements PayloadAction<any> {
    constructor(public readonly payload: AdvancedFilters<AdvancedFilterField>) {
        super('Set Event Advanced Filters');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventSearchCriteria: {
                    ...state.ui.eventSearchCriteria,
                    advancedFilters: {
                        ...state.ui.eventSearchCriteria.advancedFilters,
                        filters: [...this.payload.filters],
                        filtersActive: this.payload.filtersActive
                    }
                }
            }
        };
    }
}

export class UpdateEventUISearchGroups
    extends EventActionReducer
    implements PayloadAction<any> {
    constructor(public readonly payload: BasicFilters) {
        super('Set Search Groups');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventSearchCriteria: {
                    ...state.ui.eventSearchCriteria,
                    basicFilters: {
                        ...state.ui.eventSearchCriteria.basicFilters,
                        groupList: [...this.payload.groupList],
                        checkedCount: this.payload.checkedCount
                    }
                }
            }
        };
    }
}

export class UpdateEventUISearchKeyword
    extends EventActionReducer
    implements PayloadAction<any> {
    constructor(public readonly payload: TextSearchSelection) {
        super('Set Search Keyword');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            ui: {
                ...state.ui,
                eventSearchCriteria: {
                    ...state.ui.eventSearchCriteria,
                    keyword: { ...this.payload }
                }
            }
        };
    }
}

export class GetPidDetailsRequest
    extends EventActionReducer
    implements PayloadAction<number[]> {
    constructor(public readonly payload: number[]) {
        super('Pid Details Request');
    }

    reduce(state: EventState): EventState {
        return state;
    }
}

export class GetPidDetailsRequestSuccess
    extends EventActionReducer
    implements PayloadAction<ApiResponse<Pid[]>> {
    constructor(public readonly payload: ApiResponse<Pid[]>) {
        super('Pid Details Request Success');
    }

    reduce(state: EventState): EventState {
        return {
            ...state,
            data: {
                ...state.data,
                pids: {
                    ...state.data.pids,
                    ...keyBy(this.payload.data, 'pid')
                }
            }
        };
    }
}

function deleteEvents(events, eventsToDelete): EventState['data']['events'] {
    const idMap: EventState['data']['events']['idMap'] = Object.assign(
        {},
        events.idMap
    );
    eventsToDelete.forEach(id => {
        if (idMap[id]) {
            delete idMap[id];
        }
    });
    return {
        idMap: idMap,
        idSort: difference(events.idSort, eventsToDelete)
    };
}

export class DeleteEvents
    extends EventActionReducer
    implements PayloadAction<number[]> {
    constructor(public readonly payload: number[]) {
        super('Delete Events');
    }
    reduce(state: EventState): EventState {
        return {
            ...state,
            data: {
                ...state.data,
                events: deleteEvents(state.data.events, this.payload)
            }
        };
    }
}

export class GetEventTypePicklistRequest extends EventActionReducer {
    constructor() {
        super('Get Event Type Picklist Request');
    }
    reduce(state: EventState): EventState {
        return state;
    }
}

export class GetEventTypePicklistSuccess
    extends EventActionReducer
    implements PayloadAction<string[]> {
    constructor(public readonly payload: string[]) {
        super('Get Event Type Picklist Success');
    }
    reduce(state: EventState): EventState {
        return {
            ...state,
            data: {
                ...state.data,
                eventTypes: this.payload
            }
        };
    }
}

export class GetEventTypePicklistFailure
    extends EventActionReducer
    implements PayloadAction<ApiError> {
    constructor(public readonly payload: ApiError) {
        super('Get Event Type Picklist Failure');
    }
    reduce(state: EventState): EventState {
        return state;
    }
}

export class ClearEventListStoreData extends EventActionReducer {
    constructor() {
        super('Clear Event List Store Data');
    }

    reduce(state: EventState): EventState {
        return {
            ui: {
                ...state.ui,
                eventGrid: {
                    ...state.ui.eventGrid,
                    total: null
                },
                eventDetail: cloneDeep(initialEventState.ui.eventDetail),
                eventCreate: cloneDeep(initialEventState.ui.eventCreate)
            },
            data: cloneDeep(initialEventState.data)
        };
    }
}

const typedReducer = createFeatureReducer(
    EventActionReducer,
    initialEventState
);

export function eventReducer(state: any, action: any) {
    return typedReducer(state, action);
}
